import { makeAutoObservable, reaction } from 'mobx';

import catalogStore from './catalog-store';
import globalStore from './global-store';

import { DEFAULT_COUNTRY_CODE, InputType, Regex } from 'src/constants';
import { getInputProps, getPersonName } from 'src/utils';
import type { ICategoryWithItems, IClientInfo } from 'src/interfaces';

type TCartLots = Record<string, number>;

class CartStore {
  init() {
    this.loadCart();

    reaction(
      () => JSON.stringify(this.lots),
      (cartString) => {
        globalStore.setCartString(cartString);
      }
    );
  }

  isOpenCart = false;
  clientInfo: IClientInfo | null = null;
  categoriesWithItems: ICategoryWithItems[] = [];
  lots: TCartLots = {};
  isLoaded = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    reaction(
      () => catalogStore.categoriesWithItems,
      (items) => {
        this.categoriesWithItems = items;
        this.checkLotsMaxAmount();
        this.isLoaded = true;
      }
    );

    this.init();
  }

  loadCart() {
    if (!globalStore.cartString) {
      return;
    }

    try {
      const lots = JSON.parse(decodeURIComponent(globalStore.cartString)) as TCartLots;
      this.setLots(
        Object.entries(lots).reduce<TCartLots>((validLots, [lotId, count]) => {
          const countNumber = parseInt(`${count}`, 10);
          if (countNumber > 0) {
            validLots[lotId] = countNumber;
          }
          return validLots;
        }, {})
      );
    } catch (e) {
      console.log(e);
    }
  }

  cleanCart() {
    this.clientInfo = null;
    this.setLots();
  }

  openCart() {
    if (!this.isCartEmpty) {
      this.isOpenCart = true;
    }
  }

  closeCart() {
    this.isOpenCart = false;
  }

  setLots(lots: TCartLots = {}) {
    this.lots = lots;
  }

  removeLot(lotId: string) {
    delete this.lots[lotId];
  }

  setLotCount(lotId: string, count: number) {
    this.lots[lotId] = count;
  }

  decrementLot(lotId: string, noRemoveEmptyLot = false) {
    const updatedCount = Math.max(this.getCountInCart(lotId) - 1, 0);
    this.setLotCount(lotId, updatedCount);
    if (!updatedCount && !noRemoveEmptyLot) {
      this.removeLot(lotId);
    }
  }

  incrementLot(lotId: string) {
    this.setLotCount(lotId, this.getCountInCart(lotId) + 1);
  }

  get cartData() {
    return this.categoriesWithItems
      .map(({ title, items }) => ({
        title,
        lots: items
          .map((item) => ({ ...item, count: this.lots[item.id] }))
          .filter(({ count }) => count !== undefined),
      }))
      .filter(({ lots }) => lots.length > 0);
  }

  get clientName() {
    return this.clientInfo ? getPersonName(this.clientInfo) : '';
  }

  get totalSum() {
    return Object.entries(this.lots).reduce(
      (sum, [lotId, count]) => sum + this.getLotPrice(lotId) * count,
      0
    );
  }

  get isCartEmpty() {
    return this.lotsCount === 0;
  }

  get initialFormValues() {
    const { isNoSlug } = globalStore;
    return {
      firstName: getInputProps(InputType.FIRST_NAME, true, this.clientInfo?.firstName),
      lastName: getInputProps(InputType.LAST_NAME, true, this.clientInfo?.lastName),
      phone: getInputProps(
        InputType.PHONE,
        true,
        this.clientInfo?.phone.toString().replace(Regex.PHONE_RAW, Regex.PHONE_PATTERN)
      ),
      countryCode: getInputProps(
        InputType.COUNTRY_CODE,
        false,
        this.clientInfo?.countryCode || DEFAULT_COUNTRY_CODE
      ),
      email: getInputProps(InputType.EMAIL, true, this.clientInfo?.email),
      message: getInputProps(InputType.MESSAGE, false, this.clientInfo?.message),

      cemetery: getInputProps(InputType.SELECT, isNoSlug, this.clientInfo?.cemetery),
      decedent: getInputProps(InputType.NAME, isNoSlug, this.clientInfo?.decedent),
      yearOfBirth: getInputProps(InputType.YEAR, false, this.clientInfo?.yearOfBirth),
      yearOfDeath: getInputProps(InputType.YEAR, false, this.clientInfo?.yearOfDeath),
    };
  }

  get isCheckoutRoutesRedirect() {
    return this.isLoaded && !this.cartData.length;
  }

  get lotsCount() {
    return Object.keys(this.lots).length;
  }

  getLotPrice(lotId: string) {
    return catalogStore.getItemPrice(lotId);
  }

  getCountInCart(lotId: string) {
    return this.lots[lotId] || 0;
  }

  setClientInfo(info: IClientInfo) {
    this.clientInfo = info;
  }

  checkLotsMaxAmount() {
    Object.entries(this.lots).forEach(([lotId, count]) => {
      const maxCount = catalogStore.items[lotId]?.maxAmount;
      if (!maxCount) {
        this.removeLot(lotId);
      } else if (count > maxCount) {
        this.setLotCount(lotId, maxCount);
      }
    });
  }
}

export default new CartStore();
